import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useOnboardingDetail } from '@/hooks/useOnboardings'
import { useAuthState } from '@/hooks/useAuth'
import { auth, db, checkUserExistsByEmail, signOutUser } from '@/lib/firebase'
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth'
import { doc, setDoc } from 'firebase/firestore'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, ArrowRight, Save, Rocket, CheckCircle, LogOut, AlertCircle } from 'lucide-react'

// Step components
import { ArtistOnboardingAccountStep } from '@/components/artist/ArtistOnboardingAccountStep'
import { ArtistOnboardingStep1 } from '@/components/artist/ArtistOnboardingStep1'
import { ArtistOnboardingStep2 } from '@/components/artist/ArtistOnboardingStep2'
import { ArtistOnboardingStep3 } from '@/components/artist/ArtistOnboardingStep3'
import { ArtistOnboardingStep4 } from '@/components/artist/ArtistOnboardingStep4'

// Validation
import { ValidationErrorDialog } from '@/components/shared/ValidationErrorDialog'
import { 
  validateProfile,
  validateStep2, 
  validateStep3, 
  validateStep4,
  validatePublish,
  type ValidationError 
} from '@/utils/onboardingValidation'

// Admin emails that can access any onboarding without overwriting the linked account
const ADMIN_EMAILS = [
  'jari@getmoretats.com',
  'keijo@getmoretats.com',
  'lotta@getmoretats.com'
]

const isAdminUser = (email: string | null | undefined): boolean => {
  if (!email) return false
  return ADMIN_EMAILS.includes(email.toLowerCase())
}

export default function ArtistOnboardingFlowPage() {
  const { onboardingId } = useParams<{ onboardingId: string }>()
  const navigate = useNavigate()
  const { onboarding, loading, error, updateOnboarding } = useOnboardingDetail(onboardingId)
  const { user } = useAuthState()
  
  const [currentStep, setCurrentStep] = useState(1)
  const [saving, setSaving] = useState(false)
  const [stepData, setStepData] = useState<any>({})
  const [userCreated, setUserCreated] = useState(false)
  const [accessDenied, setAccessDenied] = useState(false)
  const [showLoginForm, setShowLoginForm] = useState(false)
  const [loginEmail, setLoginEmail] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)
  const [existingAccountEmail, setExistingAccountEmail] = useState<string | null>(null)
  
  // Validation error dialog state
  const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false)
  const [validationErrorTitle, setValidationErrorTitle] = useState('')
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([])

  // Initialize current step from database
  useEffect(() => {
    if (onboarding?.currentStep) {
      setCurrentStep(onboarding.currentStep)
    }
  }, [onboarding])

  // Access control: check if user has permission to access this onboarding
  useEffect(() => {
    if (!onboarding || loading) return

    // Allow admin users to access any onboarding (for helping artists)
    if (user && isAdminUser(user.email)) {
      console.log('🔓 Admin user detected, granting access to onboarding')
      setAccessDenied(false)
      return
    }

    // If onboarding has no userId yet, anyone can access step 1 (to create account)
    // But once Step 1 is done (userId is set), Step 1 should not be accessible anymore
    if (!onboarding.userId) {
      // Anyone can access Step 1 to create account
      if (currentStep === 1) {
        setAccessDenied(false)
        return
      }
      // Steps 2+ require account to be created first
      if (currentStep > 1) {
        setAccessDenied(true)
        return
      }
    }

    // If onboarding has userId (Step 1 completed)
    if (onboarding.userId) {
      // If trying to access Step 1 after it's been completed, redirect to Step 2
      if (currentStep === 1) {
        console.log('Step 1 already completed, redirecting to Step 2')
        setCurrentStep(2)
        return
      }
      
      // For steps 2+, only allow the linked user
      if (user?.uid !== onboarding.userId) {
        setAccessDenied(true)
        return
      }
    }

    setAccessDenied(false)
  }, [onboarding, user, currentStep, loading])

  const steps = [
    { number: 1, title: 'Account', description: 'Create or link your account' },
    { number: 2, title: 'Profile', description: 'Review your profile information' },
    { number: 3, title: 'Portfolio', description: 'Select 10-20 tattoo images' },
    { number: 4, title: 'Flashes', description: 'Upload and price your flashes' },
    { number: 5, title: 'Agreement', description: 'Accept the Artist Agreement' }
  ]

  const handleStepDataChange = (data: any) => {
    // Only log when email or password changes to reduce spam
    if (data.email !== stepData[currentStep]?.email || data.password !== stepData[currentStep]?.password) {
      console.log('📝 [OnboardingFlow] Key data change:', {
        currentStep,
        email: data.email,
        hasPassword: !!data.password,
        hasConfirmPassword: !!data.confirmPassword
      })
    }
    
    setStepData((prev: any) => ({
      ...prev,
      [currentStep]: data
    }))
  }

  const handleLogout = async () => {
    try {
      console.log('[OnboardingFlow] Starting sign-out...')
      
      // Clean up iOS-specific resources first
      const { cleanupIosOnSignOut } = await import('@/lib/notifications')
      const { Capacitor } = await import('@capacitor/core')
      const isIOSNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios'
      
      if (isIOSNative) {
        console.log('[OnboardingFlow] Cleaning up iOS resources...')
        await cleanupIosOnSignOut()
      }
      
      // Sign out from Firebase
      console.log('[OnboardingFlow] Signing out from Firebase...')
      await signOutUser()
      
      console.log('[OnboardingFlow] Sign-out successful')
      setAccessDenied(false)
    } catch (error: any) {
      console.error('[OnboardingFlow] Error signing out:', error)
      console.error('[OnboardingFlow] Error details:', {
        code: error?.code,
        message: error?.message,
        name: error?.name
      })
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!loginEmail || !loginPassword) return

    setLoginLoading(true)
    setLoginError(null)

    try {
      const userCredential = await signInWithEmailAndPassword(auth, loginEmail, loginPassword)
      console.log('User logged in successfully:', userCredential.user.uid)
      
      // Only update the userId if the onboarding doesn't already have one
      // This prevents admins from accidentally overwriting the linked account
      const updates: any = {}
      if (!onboarding?.userId) {
        updates.userId = userCredential.user.uid
        updates.email = loginEmail
        console.log('✅ Linking user account to onboarding')
      } else {
        console.log('⚠️ Onboarding already has a userId, not overwriting')
      }
      
      if (Object.keys(updates).length > 0) {
        await updateOnboarding(updates)
      }
      
      // Hide login form and proceed
      setShowLoginForm(false)
      setUserCreated(true)
      setExistingAccountEmail(null)
      setLoginEmail('')
      setLoginPassword('')
      
      // Proceed to next step
      if (currentStep === 1) {
        setCurrentStep(2)
      }
    } catch (error: any) {
      console.error('Login error:', error)
      
      // Provide user-friendly error messages based on Firebase Auth error codes
      let userFriendlyMessage = 'Failed to login. Please try again.'
      
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        userFriendlyMessage = 'Incorrect password. Please check your password and try again.'
      } else if (error.code === 'auth/user-not-found') {
        userFriendlyMessage = 'No account found with this email address.'
      } else if (error.code === 'auth/too-many-requests') {
        userFriendlyMessage = 'Too many failed attempts. Please try again later.'
      } else if (error.code === 'auth/user-disabled') {
        userFriendlyMessage = 'This account has been disabled. Please contact support.'
      } else if (error.code === 'auth/invalid-email') {
        userFriendlyMessage = 'Invalid email address format.'
      } else if (error.message) {
        userFriendlyMessage = error.message
      }
      
      setLoginError(userFriendlyMessage)
      await updateOnboarding({ latestError: `Login failed: ${userFriendlyMessage}` })
    } finally {
      setLoginLoading(false)
    }
  }

  const createFirebaseUser = async (email: string, password: string) => {
    if (!onboarding) return null

    try {
      console.log('Creating Firebase user for:', email)
      
      // Create Firebase user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      const user = userCredential.user

      console.log('Firebase user created, creating user document...')
      
      // Create user document in Firestore
      const now = new Date()
      await setDoc(doc(db, 'users', user.uid), {
        createdAt: now,
        email: email,
        instagram: onboarding.displayName,
        lastLogin: now,
        isArtist: true
        // Note: artistId will be set when the artist is created from this onboarding
        // and userId will be added to the artist document by the backend
      })

      console.log('User document created, updating onboarding...')
      
      // Save userId to onboarding document only if it doesn't already have one
      // This prevents admins from accidentally overwriting the linked account
      if (!onboarding.userId) {
        await updateOnboarding({
          userId: user.uid,
          email: email
        })
        console.log('✅ Linked new user account to onboarding:', user.uid)
      } else {
        console.log('⚠️ Onboarding already has a userId, not overwriting')
      }

      console.log('Firebase user created and connected successfully:', user.uid)
      return user
    } catch (error) {
      console.error('Error creating Firebase user:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to create user account'
      // Don't try to update onboarding here if user creation failed
      throw new Error(`Account creation failed: ${errorMessage}`)
    }
  }

  const handleSave = async () => {
    if (!onboarding) return

    console.log('[OnboardingFlow] Saving step:', {
      currentStep,
      stepData: stepData[currentStep],
      step3FlashCount: currentStep === 3 ? stepData[3]?.flashes?.length || 0 : 'N/A'
    })

    setSaving(true)
    try {
      const updates = {
        ...stepData[currentStep],
        currentStep: currentStep
      }
      
      // SECURITY: Never send password fields to database
      delete (updates as any).password
      delete (updates as any).confirmPassword
      delete (updates as any).userAccountExists
      
      // For step 1 (Account), store the email in the onboarding document
      // This is needed for security rules and to link the account
      if (currentStep === 1 && stepData[1]?.email) {
        updates.email = stepData[1].email
      }
      
      console.log('[OnboardingFlow] Sending updates to database:', {
        currentStep,
        updateKeys: Object.keys(updates),
        flashCount: updates.flashes?.length || 'N/A'
      })
      
      await updateOnboarding(updates)
      console.log('[OnboardingFlow] Save successful')
    } catch (err) {
      console.error('[OnboardingFlow] Error saving step:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to save step data'
      await updateOnboarding({ latestError: `Save failed (Step ${currentStep}): ${errorMessage}` })
      throw err // Re-throw to be handled by calling function
    } finally {
      setSaving(false)
    }
  }

  const handleNext = async () => {
    console.log('🚀 handleNext called', { currentStep, userCreated, stepData: stepData[1] })
    
    try {
      // Check if admin is viewing someone else's onboarding (skip password validation)
      const isAdminViewingOthersOnboarding = !!(user && isAdminUser(user.email) && onboarding?.userId && user.uid !== onboarding.userId)
      
      // Skip password validation if user is already logged in with the correct account
      const isUserLoggedInWithCorrectAccount = !!(user && onboarding?.userId && user.uid === onboarding.userId)
      const shouldSkipPasswordValidation = isAdminViewingOthersOnboarding || isUserLoggedInWithCorrectAccount
      
      if (shouldSkipPasswordValidation && currentStep === 1) {
        console.log('✅ Skipping password validation - user already authenticated', {
          isAdmin: isAdminViewingOthersOnboarding,
          isCorrectAccount: isUserLoggedInWithCorrectAccount
        })
      }
      
      // Validate current step before proceeding
      let validationResult
      
      switch (currentStep) {
        case 1:
          // Step 1: Account creation - validate email and password
          const accountStepData = stepData[1] || {}
          const step1UserAccountExists = accountStepData.userAccountExists
          
          // Basic email validation
          if (!accountStepData.email?.trim()) {
            setValidationErrorTitle('Step 1: Account - Missing Email')
            setValidationErrors([{ field: 'Email', message: 'Please enter your email address' }])
            setShowValidationErrorDialog(true)
            return
          }
          
          // Password validation
          if (!shouldSkipPasswordValidation) {
            if (!accountStepData.password?.trim()) {
              setValidationErrorTitle('Step 1: Account - Missing Password')
              setValidationErrors([{ field: 'Password', message: 'Please enter your password' }])
              setShowValidationErrorDialog(true)
              return
            }
            
            // For new accounts, check password confirmation
            if (step1UserAccountExists === false) {
              if (accountStepData.password !== accountStepData.confirmPassword) {
                setValidationErrorTitle('Step 1: Account - Password Mismatch')
                setValidationErrors([{ field: 'Password', message: 'Passwords do not match' }])
                setShowValidationErrorDialog(true)
                return
              }
            }
          }
          break
        case 2:
          // Step 2: Profile - validate profile fields only (no email/password)
          // First check authentication: user must be logged in as the linked account or be an admin
          if (!user) {
            setValidationErrorTitle('Step 2: Authentication Required')
            setValidationErrors([{ field: 'Authentication', message: 'You must be logged in to continue. Please go back to Step 1 and complete the account setup.' }])
            setShowValidationErrorDialog(true)
            return
          }
          
          // Check if user is either the linked account or an admin
          const isLinkedUserForStep2 = onboarding?.userId && user.uid === onboarding.userId
          const isAdminForStep2 = isAdminUser(user.email)
          
          if (!isLinkedUserForStep2 && !isAdminForStep2) {
            setValidationErrorTitle('Step 2: Access Denied')
            setValidationErrors([{ field: 'Authentication', message: 'You must be logged in as the linked account to continue. Please go back to Step 1 and log in with the correct account.' }])
            setShowValidationErrorDialog(true)
            return
          }
          
          // Now validate the profile fields
          validationResult = validateProfile(stepData[2] || {})
          if (!validationResult.isValid) {
            setValidationErrorTitle('Step 2: Profile Information - Missing Required Fields')
            setValidationErrors(validationResult.errors)
            setShowValidationErrorDialog(true)
            return
          }
          break
        case 3:
          validationResult = validateStep2(stepData[3] || {})
          if (!validationResult.isValid) {
            setValidationErrorTitle('Step 3: Portfolio Images - Requirements Not Met')
            setValidationErrors(validationResult.errors)
            setShowValidationErrorDialog(true)
            return
          }
          break
        case 4:
          console.log('[OnboardingFlow] Validating step 4 (flashes):', {
            stepData4: stepData[4],
            stepData4FlashCount: stepData[4]?.flashes?.length || 0,
            onboardingFlashCount: onboarding?.flashes?.length || 0,
            onboardingFlashes: onboarding?.flashes?.map(f => ({ id: f.id, minPrice: f.minPrice, minSize: f.minSize }))
          })
          validationResult = validateStep3(stepData[4] || {}, onboarding ?? undefined)
          if (!validationResult.isValid) {
            setValidationErrorTitle('Step 4: Flash Tattoos - Issues Found')
            setValidationErrors(validationResult.errors)
            setShowValidationErrorDialog(true)
            return
          }
          break
        case 5:
          validationResult = validateStep4(stepData[5] || {})
          if (!validationResult.isValid) {
            setValidationErrorTitle('Step 5: Agreement - Not Accepted')
            setValidationErrors(validationResult.errors)
            setShowValidationErrorDialog(true)
            return
          }
          break
      }
      
      // Handle user account creation/connection when moving from step 1 to step 2
      if (currentStep === 1 && !userCreated) {
        console.log('📝 Processing step 1 -> 2 transition')
        const currentStepData = stepData[1]
        console.log('📋 Current step data:', currentStepData)
        
        if (!currentStepData?.email) {
          console.error('❌ Email is required')
          setValidationErrorTitle('Email Required')
          setValidationErrors([{ field: 'Email', message: 'Email address is required to continue' }])
          setShowValidationErrorDialog(true)
          return
        }

        try {
          setSaving(true)
          
          // Check if user account exists
          let userExists = false
          try {
            userExists = await checkUserExistsByEmail(currentStepData.email)
            console.log('Email check result:', { email: currentStepData.email, exists: userExists })
          } catch (emailCheckError) {
            console.warn('Email check failed, will attempt account creation and handle accordingly:', emailCheckError)
            // If email check fails, we'll try to create account and handle "email-already-in-use" error
            userExists = false
          }
          
          if (userExists) {
            console.log('✅ User exists, checking login status...', { 
              currentUser: user?.email, 
              targetEmail: currentStepData.email,
              isLoggedInWithSameEmail: user && user.email === currentStepData.email
            })
            
            // Check if user is currently logged in with the same email
            if (user && user.email === currentStepData.email) {
              // User is already logged in with the correct account
              console.log('✅ User already logged in with correct account')
              setUserCreated(true)
            } else {
              // User needs to log in - try to log in with provided password
              if (!currentStepData.password) {
                console.log('🔐 No password provided, showing login form')
                setExistingAccountEmail(currentStepData.email)
                setLoginEmail(currentStepData.email)
                setShowLoginForm(true)
                setSaving(false)
                return
              }
              
              console.log('🔐 Attempting login with provided credentials')
              try {
                const userCredential = await signInWithEmailAndPassword(auth, currentStepData.email, currentStepData.password)
                console.log('✅ Login successful:', userCredential.user.uid)
                
                // Only update userId if not already set (prevent admin override)
                const updates: any = { email: currentStepData.email }
                if (!onboarding?.userId) {
                  updates.userId = userCredential.user.uid
                  console.log('✅ Linking user account to onboarding:', userCredential.user.uid)
                } else {
                  console.log('⚠️ Onboarding already has userId, not overwriting')
                }
                
                await updateOnboarding(updates)
                
                setUserCreated(true)
                console.log('✅ User connected to onboarding')
              } catch (loginError: any) {
                console.error('❌ Login failed:', loginError)
                
                // Show user-friendly error message
                let errorMessage = 'Login failed. Please check your password.'
                if (loginError.code === 'auth/wrong-password' || loginError.code === 'auth/invalid-credential') {
                  errorMessage = 'Incorrect password. Please check your password and try again.'
                } else if (loginError.code === 'auth/too-many-requests') {
                  errorMessage = 'Too many failed attempts. Please try again later.'
                }
                
                setValidationErrorTitle('Login Failed')
                setValidationErrors([{ field: 'Account Login', message: errorMessage }])
                setShowValidationErrorDialog(true)
                setSaving(false)
                return
              }
            }
          } else {
            // Try to create new account (or handle if account already exists)
            if (!currentStepData.password) {
              console.error('Password is required for new accounts')
              setSaving(false)
              return
            }
            
            // Check password confirmation for new accounts
            if (currentStepData.confirmPassword && currentStepData.password !== currentStepData.confirmPassword) {
              console.error('Passwords do not match')
              setValidationErrorTitle('Password Mismatch')
              setValidationErrors([{ field: 'Confirm Password', message: 'Passwords do not match. Please ensure both password fields match.' }])
              setShowValidationErrorDialog(true)
              setSaving(false)
              return
            }
            
            try {
              // Create the Firebase user account first
              await createFirebaseUser(currentStepData.email, currentStepData.password)
              setUserCreated(true)
            } catch (createError: any) {
              // Handle case where account already exists (email check failed but account exists)
              if (createError.message?.includes('email-already-in-use') || createError.message?.includes('auth/email-already-in-use')) {
                console.log('Account already exists, showing login form')
                setExistingAccountEmail(currentStepData.email)
                setLoginEmail(currentStepData.email)
                setShowLoginForm(true)
                setSaving(false)
                return
              } else {
                throw createError // Re-throw other errors
              }
            }
          }
        } catch (error) {
          console.error('❌ Failed to handle user account:', error)
          const errorMessage = error instanceof Error ? error.message : 'Failed to handle user account'
          setValidationErrorTitle('Account Error')
          setValidationErrors([{ field: 'Account Creation/Login', message: errorMessage }])
          setShowValidationErrorDialog(true)
          // Only try to update onboarding if we have a user context
          if (user) {
            await updateOnboarding({ latestError: errorMessage })
          }
          setSaving(false)
          return
        } finally {
          setSaving(false)
        }
      }
      
      console.log('✅ User authentication completed, saving step data...')
      
      // Now save current step data AFTER user is authenticated
      await handleSave()
      
      console.log('✅ Step data saved, moving to next step...')
      
      if (currentStep < 5) {
        setCurrentStep(currentStep + 1)
        console.log('✅ Moved to step:', currentStep + 1)
      }
    } catch (error) {
      console.error('❌ Error in handleNext:', error)
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
      setValidationErrorTitle('Unexpected Error')
      setValidationErrors([{ field: 'System Error', message: errorMessage }])
      setShowValidationErrorDialog(true)
      // Only try to update onboarding if we have a user context
      if (user) {
        await updateOnboarding({ latestError: errorMessage })
      }
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handlePublish = async () => {
    if (!onboarding) return

    console.log('[OnboardingFlow] Starting publish process')

    try {
      // Save current step data first (if there's any local state to save)
      // This ensures validation checks the most up-to-date data
      if (Object.keys(stepData[currentStep] || {}).length > 0) {
        console.log('[OnboardingFlow] Saving current step data before validation')
        await handleSave()
      }

      // Check if admin is viewing someone else's onboarding (skip password validation)
      const isAdminViewingOthersOnboarding = !!(user && isAdminUser(user.email) && onboarding?.userId && user.uid !== onboarding.userId)
      
      // Skip password validation if user is already logged in with the correct account
      // This handles the case where user created account, did some steps, then reloaded the page
      const isUserLoggedInWithCorrectAccount = !!(user && onboarding?.userId && user.uid === onboarding.userId)
      const shouldSkipPasswordValidation = isAdminViewingOthersOnboarding || isUserLoggedInWithCorrectAccount
      
      if (shouldSkipPasswordValidation) {
        console.log('✅ Skipping password validation during publish - user already authenticated', {
          isAdmin: isAdminViewingOthersOnboarding,
          isCorrectAccount: isUserLoggedInWithCorrectAccount
        })
      }
      
      // Validate all steps before publishing
      // IMPORTANT: Use onboarding document data (from Firestore) instead of local stepData
      // This ensures validation works even after page refresh
      const validationData = {
        1: {
          // Profile data from onboarding document
          firstName: onboarding.firstName,
          lastName: onboarding.lastName,
          email: onboarding.email,
          bio: onboarding.bio,
          location: onboarding.location,
          lat: onboarding.lat,
          lon: onboarding.lon,
          profileImageUrl: onboarding.profileImageUrl,
          backdropUrl: onboarding.backdropUrl,
        },
        2: {
          // Portfolio data from onboarding document
          tattooImageUrls: onboarding.tattooImageUrls
        },
        3: {
          // Flash data from onboarding document
          flashes: onboarding.flashes
        },
        4: {
          // Agreement data from onboarding document
          agreementAccepted: onboarding.agreementAccepted
        }
      }
      
      console.log('[OnboardingFlow] Validating with onboarding document data:', {
        hasFirstName: !!onboarding.firstName,
        hasLastName: !!onboarding.lastName,
        hasEmail: !!onboarding.email,
        hasBio: !!onboarding.bio,
        hasLocation: !!onboarding.location,
        hasCoordinates: !!(onboarding.lat && onboarding.lon),
        hasProfileImage: !!onboarding.profileImageUrl,
        hasBanner: !!onboarding.backdropUrl,
        portfolioImageCount: onboarding.tattooImageUrls?.length || 0,
        flashCount: onboarding.flashes?.length || 0,
        agreementAccepted: onboarding.agreementAccepted
      })
      
      const publishValidation = validatePublish(validationData, onboarding ?? undefined, true, shouldSkipPasswordValidation)
      
      if (!publishValidation.isValid) {
        console.error('[OnboardingFlow] Publish validation failed:', publishValidation.errors)
        setValidationErrorTitle('Cannot Publish - Issues Found')
        setValidationErrors(publishValidation.errors)
        setShowValidationErrorDialog(true)
        await updateOnboarding({ 
          latestError: `Publish validation failed: ${publishValidation.errors.map(e => `${e.field}: ${e.message}`).join('; ')}` 
        })
        return
      }

      console.log('[OnboardingFlow] All validations passed, proceeding with publish')

      setSaving(true)
      
      // Update status to waiting for review
      // Don't merge stepData - all data is already in the onboarding document
      const updates = {
        status: 'waiting_for_review' as const,
        currentStep: 5
      }
      
      console.log('[OnboardingFlow] Publishing onboarding:', {
        onboardingId: onboarding.id,
        status: updates.status
      })
      
      await updateOnboarding(updates)
      
      console.log('[OnboardingFlow] Publish successful, navigating to success page')
      // Navigate to success page or dashboard
      navigate('/onboarding/success')
    } catch (err) {
      console.error('[OnboardingFlow] Error publishing onboarding:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to publish profile'
      await updateOnboarding({ latestError: `Publish failed: ${errorMessage}` })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground mx-auto mb-4"></div>
          <p className="text-foreground">Loading your onboarding...</p>
        </div>
      </div>
    )
  }

  if (error || !onboarding) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Onboarding Not Found</CardTitle>
            <CardDescription>
              {error?.message || 'The onboarding you\'re looking for doesn\'t exist or isn\'t ready.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/onboarding')} className="w-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Code Entry
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (onboarding.status !== 'ready_for_artist' && onboarding.status !== 'completed') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Onboarding Not Ready</CardTitle>
            <CardDescription>
              This onboarding is not ready yet. Please wait for our team to prepare it.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/onboarding')} className="w-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Code Entry
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Access denied screen
  if (accessDenied) {
    const isWrongUser = user && onboarding.userId && user.uid !== onboarding.userId
    const isLoggedInButShouldntBe = user && !onboarding.userId && currentStep > 1
    const needsLogin = !user && onboarding.userId
    const needsCorrectLogin = (isWrongUser || needsLogin) // Either wrong user or no user, but onboarding has userId

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              {isWrongUser && (
                <>You are logged in as <strong>{user.email}</strong>, but this onboarding belongs to a different account.</>
              )}
              {isLoggedInButShouldntBe && (
                <>You need to be logged out to start this onboarding process.</>
              )}
              {needsLogin && (
                <>You need to be logged in as the correct user to access this onboarding.</>
              )}
              {isWrongUser && (
                <>Please logout and login with the correct account, or login directly below.</>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Login Form */}
            {needsCorrectLogin && showLoginForm && (
              <form onSubmit={handleLogin} className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="Enter your email"
                    readOnly={!!existingAccountEmail}
                    className={existingAccountEmail ? 'bg-muted cursor-not-allowed' : ''}
                    required
                  />
                  {existingAccountEmail && (
                    <p className="text-xs text-muted-foreground">
                      Please log in to your existing account with this email address
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <Input
                    id="login-password"
                    type="password"
                    value={loginPassword}
                    onChange={(e) => {
                      setLoginPassword(e.target.value)
                      // Clear error when user starts typing
                      if (loginError) setLoginError(null)
                    }}
                    placeholder="Enter your password"
                    required
                  />
                </div>
                {loginError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                    <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-700">{loginError}</p>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    disabled={loginLoading || !loginEmail || !loginPassword}
                    className="flex-1"
                  >
                    {loginLoading ? 'Logging in...' : 'Login'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowLoginForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}

            {/* Action Buttons */}
            {!showLoginForm && (
              <>
                {needsCorrectLogin && (
                  <Button onClick={() => setShowLoginForm(true)} className="w-full">
                    Login to Continue
                  </Button>
                )}
                {user && (
                  <Button onClick={handleLogout} variant="outline" className="w-full">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout and Continue
                  </Button>
                )}
                <Button onClick={() => navigate('/onboarding')} variant="outline" className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Code Entry
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    )
  }

  const progress = currentStep * 20 // 20% per step (5 steps total)

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Account Information Display */}
        <div className="flex justify-end mb-4">
          <div className="flex flex-col gap-2 text-xs text-muted-foreground">
            {/* Linked Account Info */}
            <div className="flex items-center gap-2">
              <span className="font-medium">Account linked to:</span>
              <span>{onboarding?.email || 'Not linked yet'}</span>
            </div>

            {/* Current User Status Display */}
            <div className="flex items-center gap-2">
              <span className="font-medium">
                {user ? (
                  <>Logged in as: {user.email}</>
                ) : (
                  <>Not logged in</>
                )}
              </span>
              {user && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground ml-1"
                >
                  <LogOut className="w-3 h-3 mr-1" />
                  Logout
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Admin Access Indicator */}
        {user && isAdminUser(user.email) && onboarding?.userId && user.uid !== onboarding.userId && (
          <Card className="mb-4 border-blue-500 bg-blue-50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900 mb-1">
                    Admin View Mode
                  </p>
                  <p className="text-xs text-blue-800">
                    You're viewing this onboarding as an admin. This onboarding is linked to a different account and your changes won't overwrite the linked user.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold">Welcome, {onboarding.displayName}!</h1>
              <p className="text-muted-foreground">Let's complete your artist profile</p>
            </div>
            <Badge variant="outline" className="font-mono">
              Code: {onboarding.code}
            </Badge>
          </div>
          
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Step {currentStep} of 5</span>
              <span>{Math.round(progress)}% Complete</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Steps Progress */}
          <div className="lg:col-span-1">
            <Card className="sticky top-8">
              <CardHeader>
                <CardTitle className="text-lg">Progress</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {steps.map((step) => {
                  // Prevent clicking on Step 1 if it's already completed (userId exists)
                  const isStep1Locked = step.number === 1 && !!onboarding?.userId
                  const canClick = !isStep1Locked
                  
                  return (
                  <div
                    key={step.number}
                    onClick={() => canClick && setCurrentStep(step.number)}
                    className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                      canClick ? 'cursor-pointer hover:bg-primary/5' : 'cursor-not-allowed opacity-50'
                    } ${
                      step.number === currentStep
                        ? 'bg-primary/10 border border-primary/20'
                        : step.number < currentStep
                        ? 'bg-green-50 border border-green-200 hover:bg-green-100'
                        : 'bg-muted/50 hover:bg-muted/70'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                      step.number === currentStep
                        ? 'bg-primary text-primary-foreground'
                        : step.number < currentStep
                        ? 'bg-green-500 text-white'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {step.number < currentStep ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        step.number
                      )}
                    </div>
                    <div className="flex-1">
                      <div className={`font-medium ${
                        step.number === currentStep ? 'text-primary' : ''
                      }`}>
                        {step.title}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {isStep1Locked ? 'Completed (locked)' : step.description}
                      </div>
                    </div>
                  </div>
                  )
                })}
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
              {/* Step Content */}
              {currentStep === 1 && (
                <ArtistOnboardingAccountStep
                  onboarding={onboarding}
                  onDataChange={handleStepDataChange}
                />
              )}
              {currentStep === 2 && (
                <ArtistOnboardingStep1
                  onboarding={onboarding}
                  onDataChange={handleStepDataChange}
                />
              )}
              {currentStep === 3 && (
                <ArtistOnboardingStep2
                  onboarding={onboarding}
                  onDataChange={handleStepDataChange}
                />
              )}
              {currentStep === 4 && (
                <ArtistOnboardingStep3
                  onboarding={onboarding}
                  onDataChange={handleStepDataChange}
                />
              )}
              {currentStep === 5 && (
                <ArtistOnboardingStep4
                  onboarding={onboarding}
                  onDataChange={handleStepDataChange}
                />
              )}

            {/* Navigation */}
            <Card>
              <CardContent className="flex items-center justify-between p-6">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={currentStep === 1}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Button>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save'}
                  </Button>

                  {currentStep < 5 ? (
                    <Button
                      onClick={handleNext}
                      disabled={saving || (currentStep === 1 && (!stepData[1]?.email || !stepData[1]?.password))}
                      className="flex items-center gap-2"
                    >
                      {saving && currentStep === 1 && !userCreated ? 'Creating Account...' : 'Next'}
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handlePublish}
                      disabled={saving || !stepData[5]?.agreementAccepted}
                      className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
                    >
                      <Rocket className="w-4 h-4" />
                      {saving ? 'Publishing...' : 'Publish Profile'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Validation Error Dialog */}
      <ValidationErrorDialog
        isOpen={showValidationErrorDialog}
        onClose={() => setShowValidationErrorDialog(false)}
        title={validationErrorTitle}
        errors={validationErrors}
      />
    </div>
  )
}
